import {
  Alert,
  Anchor,
  Button,
  Container,
  PasswordInput,
  Stack,
  TextInput,
  Title,
} from "@mantine/core";
import type { FormEvent, ReactElement } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { readRedirectTarget } from "../auth/redirectTarget";
import { HTTP_UNAUTHORIZED, HttpError } from "../features/auth/api";
import { useLogin } from "../features/auth/useLogin";

// Страница `/login`.
//
// Mantine-форма email + password. На success — useLogin кладёт
// org-less identity в store (ADR-0038, фаза 1), после чего навигация
// на `/select-org` (фаза 2 — обеспечить активную организацию), а не
// сразу в раздел. `state.from` — путь, куда `/select-org` вернёт
// после выбора org (по умолчанию `/home` — развилка в первый
// доступный раздел, см. `readRedirectTarget`).
// На 401 показывается Alert «неверный email или пароль». Любой
// другой статус — generic-сообщение об ошибке.
export function LoginPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const login = useLogin();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    login.mutate(
      { email, password },
      {
        onSuccess: () => {
          // `replace: true` — кнопка «назад» из /select-org не
          // возвращает на форму логина (UX-якорь, форма теперь
          // недоступна — мы залогинены).
          void navigate("/select-org", {
            replace: true,
            state: { from: readRedirectTarget(location.state) },
          });
        },
      },
    );
  };

  // Различаем «бэк сказал 401» (плохие credentials) и другие сбои
  // (network down, 5xx). HttpError несёт status, прочее — generic.
  const errorBanner = ((): ReactElement | null => {
    if (!login.isError) {
      return null;
    }
    if (
      login.error instanceof HttpError &&
      login.error.status === HTTP_UNAUTHORIZED
    ) {
      return (
        <Alert color="red" title={t("auth.errorHeading")}>
          {t("auth.invalidCredentials")}
        </Alert>
      );
    }
    return (
      <Alert color="red" title={t("auth.errorHeading")}>
        {t("auth.genericError", { message: login.error.message })}
      </Alert>
    );
  })();

  return (
    <Container py="xl" size="xs">
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <Title order={1}>{t("auth.loginTitle")}</Title>

          <TextInput
            label={t("auth.emailLabel")}
            type="email"
            value={email}
            onChange={(event) => {
              setEmail(event.currentTarget.value);
            }}
            required
            autoComplete="email"
          />
          <PasswordInput
            label={t("auth.passwordLabel")}
            value={password}
            onChange={(event) => {
              setPassword(event.currentTarget.value);
            }}
            required
            autoComplete="current-password"
          />

          {errorBanner}

          <Button type="submit" loading={login.isPending}>
            {t("auth.loginButton")}
          </Button>

          <Anchor component={Link} to="/register" size="sm">
            {t("auth.toRegister")}
          </Anchor>
        </Stack>
      </form>
    </Container>
  );
}
